import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calendar, Clock, User, Building, Phone, Target, Users } from 'lucide-react';
import { Report, Customer } from '@/types';

interface ReportDetailDialogProps {
  report: Report | null;
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
}

const ReportDetailDialog: React.FC<ReportDetailDialogProps> = ({
  report,
  customer,
  isOpen,
  onClose
}) => {
  if (!report) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            {customer?.businessName || 'Unknown Customer'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span>{new Date(report.date).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-gray-500" />
              <span>{report.time}</span>
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Director</label>
              <div className="flex items-center gap-2 mt-1">
                <User className="h-4 w-4 text-gray-500" />
                <span>{report.director}</span>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Phone</label>
              <div className="flex items-center gap-2 mt-1">
                <Phone className="h-4 w-4 text-gray-500" />
                <span>{report.phone}</span>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Business Type</label>
              <div className="mt-1">
                <Badge variant="secondary">{report.businessType}</Badge>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Products</label>
              <div className="flex flex-wrap gap-1 mt-1">
                {report.product.map((prod, index) => (
                  <Badge key={index} variant="outline">{prod}</Badge>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Competitor</label>
              <div className="flex items-center gap-2 mt-1">
                <Users className="h-4 w-4 text-gray-500" />
                <span>{report.competitor}</span>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Goal</label>
              <div className="flex items-center gap-2 mt-1">
                <Target className="h-4 w-4 text-gray-500" />
                <span>{report.goal}</span>
              </div>
            </div>
          </div>

          <Separator />

          <div className="text-xs text-gray-500">
            Created: {new Date(report.createdAt).toLocaleString()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportDetailDialog;